import { createOptimisticSpace } from './createOptimisticSpace.js';
import { useQueryClient } from '@tanstack/react-query';
import { spaceQueryPredicates } from '../queries/spaceQueryPredicates.js';
import { mutateSpaceMeta } from './mutateSpaceMeta.js';
import { resourceQueryPredicates } from '../../resources/queries/resourceQueryPredicates.js';
import { useOptimisticallyUpdateQueriesWithPredicate } from '@fabric-app/queries/utilities';
import { useQueryUserInfo } from '../../user/queries/useQueryUserInfo.js';
import { PermissionRole } from '@fabric/woody-client';

const useQueryCacheSpaceHelpers = () => {
  const queryClient = useQueryClient();
  const optimisticallyUpdateQueries = useOptimisticallyUpdateQueriesWithPredicate();
  const { data: user } = useQueryUserInfo();
  return {
    updateCachedSpace: (nextProperties) => {
      queryClient.cancelQueries({
        predicate: (q) => spaceQueryPredicates.spacesAll(q) || spaceQueryPredicates.space(q, nextProperties.id),
        type: "active"
      });
      const optimisticallyUpdateSpaceAllQueries = optimisticallyUpdateQueries((q) => spaceQueryPredicates.spacesAll(q), (data) => {
        return data ? {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            roots: page.roots.map((root) => {
              if (root.id === nextProperties.id) {
                return {
                  ...root,
                  modifiedAt: (/* @__PURE__ */ new Date()).toISOString(),
                  folder: {
                    ...root.folder,
                    ...nextProperties
                  }
                };
              }
              return root;
            })
          }))
        } : data;
      });
      const optimisticallyUpdateDirectQueries = optimisticallyUpdateQueries((q) => resourceQueryPredicates.resourceDetail(q, nextProperties.id), (folder) => folder ? {
        ...folder,
        ...nextProperties
      } : folder);
      return {
        optimisticallyUpdateSpaceAllQueries,
        optimisticallyUpdateDirectQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticallyUpdateSpaceAllQueries.resetQueriesData();
          optimisticallyUpdateDirectQueries.resetQueriesData();
        },
        invalidateQueries: () => {
          optimisticallyUpdateSpaceAllQueries.invalidateQueries();
          optimisticallyUpdateDirectQueries.invalidateQueries();
        }
      };
    },
    deleteCachedSpaces: (spaceIds) => {
      queryClient.cancelQueries({
        predicate: (q) => spaceQueryPredicates.spacesAll(q) || spaceQueryPredicates.space(q, spaceIds.join(",")),
        type: "active"
      });
      const optimisticallyUpdateSpaceAllQueries = optimisticallyUpdateQueries((q) => spaceQueryPredicates.spacesAll(q), (data) => {
        return data ? {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            roots: page.roots.filter((root) => !spaceIds.includes(root.id))
          }))
        } : data;
      });
      const optimisticallyUpdateDirectQueries = spaceIds.map((spaceId) => optimisticallyUpdateQueries((q) => spaceQueryPredicates.space(q, spaceId), (data) => data && mutateSpaceMeta(data, {
        isDeleting: true
      })));
      return {
        optimisticallyUpdateSpaceAllQueries,
        optimisticallyUpdateDirectQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticallyUpdateSpaceAllQueries.resetQueriesData();
          optimisticallyUpdateDirectQueries.forEach((optimisticUpdateDirectQuery) => optimisticUpdateDirectQuery.resetQueriesData());
        },
        invalidateQueries: () => {
          optimisticallyUpdateSpaceAllQueries.invalidateQueries();
          optimisticallyUpdateDirectQueries.forEach((optimisticUpdateDirectQuery) => optimisticUpdateDirectQuery.invalidateQueries());
        }
      };
    },
    addNewSpaceToCache: (optimisticRootSpace) => {
      queryClient.cancelQueries({
        predicate: (q) => spaceQueryPredicates.spacesAll(q) || spaceQueryPredicates.space(q, optimisticRootSpace.id),
        type: "active"
      });
      const optimisticUpdateAllQueries = optimisticallyUpdateQueries(spaceQueryPredicates.spacesAll, (data) => {
        const nextData = data;
        if (nextData) {
          nextData.pages[0].roots.unshift(optimisticRootSpace);
        }
        return nextData;
      }, {
        type: "active"
      });
      const optimisticSpace = createOptimisticSpace(user, optimisticRootSpace.folder.name, PermissionRole.OWNER);
      const optimisticUpdateDirectQueries = optimisticallyUpdateQueries((q) => spaceQueryPredicates.space(q, optimisticSpace.id), (data) => data || optimisticSpace);
      return {
        optimisticUpdateDirectQueries,
        optimisticUpdateAllQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticUpdateAllQueries.resetQueriesData();
          optimisticUpdateDirectQueries.resetQueriesData();
        },
        invalidateQueries: () => {
          optimisticUpdateAllQueries.invalidateQueries();
          optimisticUpdateDirectQueries.invalidateQueries();
        }
      };
    }
  };
};

export { useQueryCacheSpaceHelpers };
//# sourceMappingURL=useQueryCacheSpaceHelpers.js.map
